import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Spin,
  Typography,
  message,
  Divider,
  Tag,
  Modal,
} from "antd";
import { AppstoreTwoTone, CheckCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom"; // Update import
import ProductDetail from "../Product/ProductDetail"; // Add this import at the top

const { Title } = Typography;

const TrendingItemsList = ({ refreshCartCount }) => {
  const [trendingApiData, setTrendingApiData] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const navigate = useNavigate();
  const { categoryName } = useParams(); // Get category from URL

  useEffect(() => {
    const fetchTrendingItems = async () => {
      setLoadingTrending(true);
      try {
        const tokenString = localStorage.getItem("token");
        const response = await axios.get(
          global.config.APIURL.BaseURL + "api/TrendingItems/GetTrendingItems",
          {
            headers: {
              Authorization: "Bearer " + tokenString,
            },
          }
        );
        setTrendingApiData(response.data || []);
      } catch (error) {
        message.error("Failed to load trending items.");
        setTrendingApiData([]);
      } finally {
        setLoadingTrending(false);
      }
    };
    fetchTrendingItems();
    logUserActivity();
  }, []);

  const logUserActivity = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      const jsondata = JSON.stringify({
        ScreenID: "1003",
        DetailID: "0",
      });

      let config = {
        method: "post",
        url: global.config.APIURL.BaseURL + "api/UserActivity/InsertActivity",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
        data: jsondata,
      };

      axios
        .request(config)
        .then((response) => {})
        .catch((error) => {});
    } catch (err) {}
  };

  // All category names for tag group
  const allCategoryNames = trendingApiData.map((cat) => cat.Category);

  // Select category from URL if available, otherwise select all
  useEffect(() => {
    if (trendingApiData.length > 0) {
      if (categoryName && allCategoryNames.includes(categoryName)) {
        setSelectedCategories([categoryName]);
      } else {
        setSelectedCategories(allCategoryNames);
      }
    }
    // eslint-disable-next-line
  }, [trendingApiData, categoryName]);

  // Handle tag change
  const handleCategoryChange = (cat) => {
    if (selectedCategories.includes(cat)) {
      setSelectedCategories(selectedCategories.filter((c) => c !== cat));
    } else {
      setSelectedCategories([
        ...selectedCategories.filter((c) => c !== cat),
        cat,
      ]);
    }
  };

  // Handle "All" tag
  const isAllChecked =
    selectedCategories.length === allCategoryNames.length &&
    allCategoryNames.length > 0;

  // Filtered categories to display:
  // - If all categories are selected (default), show in original order
  // - If user selects (ticks) categories, show most recently ticked at top
  const filteredData =
    selectedCategories.length === allCategoryNames.length
      ? allCategoryNames
          .map((catName) =>
            trendingApiData.find((cat) => cat.Category === catName)
          )
          .filter(Boolean)
      : [...selectedCategories]
          .reverse() // latest checked at top
          .map((catName) =>
            trendingApiData.find((cat) => cat.Category === catName)
          )
          .filter(Boolean);

  // Replace handleProductClick with:
  const handleProductClick = (cat, item) => {
    sessionStorage.setItem("ProductID", item.ProductID);
    setSelectedProductId(item.ProductID);
    setShowProductModal(true);
  };

  return (
    <div
      style={{ background: "#f7f9fb", minHeight: "100vh", padding: "32px 0" }}
    >
      <Card style={{ maxWidth: 1200, margin: "0 auto", borderRadius: 8 }}>
        <Title level={2} style={{ textAlign: "center", marginBottom: 32 }}>
          Trending Products
        </Title>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 24,
            justifyContent: "center",
          }}
        >
          {/* "All" Tag */}
          <Tag.CheckableTag
            checked={isAllChecked}
            onChange={(checked) => {
              if (checked) {
                setSelectedCategories(allCategoryNames);
              } else {
                setSelectedCategories([]);
              }
            }}
            style={{
              padding: "8px 16px",
              borderRadius: 20,
              fontWeight: 500,
              fontSize: 16,
              background: isAllChecked ? "#1677ff" : "#f0f5ff",
              color: isAllChecked ? "#fff" : "#1677ff",
              border: isAllChecked
                ? "1.5px solid #1677ff"
                : "1.5px solid #d6e4ff",
              cursor: "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            <AppstoreTwoTone
              twoToneColor={isAllChecked ? "#fff" : "#1677ff"}
              style={{ fontSize: 18 }}
            />
            All
          </Tag.CheckableTag>
          {/* Category Tags */}
          {allCategoryNames.map((cat) => (
            <Tag.CheckableTag
              key={cat}
              checked={selectedCategories.includes(cat)}
              onChange={() => handleCategoryChange(cat)}
              style={{
                padding: "8px 16px",
                borderRadius: 20,
                fontWeight: 500,
                fontSize: 13,
                background: selectedCategories.includes(cat)
                  ? "#1677ff"
                  : "#f0f5ff",
                color: selectedCategories.includes(cat) ? "#fff" : "#1677ff",
                border: selectedCategories.includes(cat)
                  ? "1.5px solid #1677ff"
                  : "1.5px solid #d6e4ff",
                cursor: "pointer",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {selectedCategories.includes(cat) && (
                <CheckCircleOutlined
                  twoToneColor="#fff"
                  style={{ fontSize: 18 }}
                />
              )}
              {cat}
            </Tag.CheckableTag>
          ))}
        </div>
        {loadingTrending ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : filteredData.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#888",
              fontSize: 18,
              margin: "32px 0",
            }}
          >
            {selectedCategories.length === 0
              ? "No category selected, select at least one category."
              : "No trending products found."}
          </div>
        ) : (
          filteredData.map((cat, idx) => (
            <div key={cat.Category || idx}>
              <Divider
                orientation="left"
                style={{ fontWeight: 600, fontSize: 18 }}
              >
                {cat.Category}
              </Divider>
              <Row gutter={[24, 24]} justify="start">
                {(cat.Products || []).map((item, pidx) => (
                  <Col
                    key={item.ProductID || pidx}
                    xs={12} // <-- two columns on mobile
                    sm={12}
                    md={6}
                    style={{
                      marginBottom: window.innerWidth < 576 ? 12 : 24, // less margin on mobile
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        height: "320px", // default for desktop
                        borderRadius: 8,
                        overflow: "hidden",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        border:
                          window.innerWidth < 576
                            ? "1.5px solid #bfbfbf" // gray outline for mobile
                            : "1px solid #f0f0f0",
                        background: "#fff",
                        cursor: "pointer",
                        ...(window.innerWidth < 576
                          ? {
                              height: "235px", // increased height for mobile
                              display: "flex",
                              flexDirection: "column",
                            }
                          : {}),
                      }}
                      onClick={() => handleProductClick(cat, item)}
                    >
                      <img
                        src={item.ImageUrl}
                        alt={item.ProductName}
                        style={{
                          width: "100%",
                          height:
                            window.innerWidth < 576
                              ? "calc(100% - 56px)"
                              : "100%",
                          objectFit: "cover",
                          display: "block",
                          borderBottomLeftRadius:
                            window.innerWidth < 576 ? 0 : 8,
                          borderBottomRightRadius:
                            window.innerWidth < 576 ? 0 : 8,
                        }}
                      />
                      {window.innerWidth < 576 ? (
                        // Mobile: name below image
                        <div
                          style={{
                            width: "100%",
                            background: "#fff",
                            color: "#222",
                            padding: "10px 8px 10px 8px", // more vertical padding
                            fontWeight: 600,
                            fontSize: 15,
                            textAlign: "left",
                            borderBottomLeftRadius: 8,
                            borderBottomRightRadius: 8,
                            minHeight: 36,
                            boxSizing: "border-box",
                            lineHeight: 1.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "normal",
                          }}
                        >
                          {item.ProductName}
                        </div>
                      ) : (
                        // Desktop: name overlay
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            bottom: 0,
                            width: "100%",
                            background: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            padding: "12px 16px 10px 12px",
                            fontWeight: 600,
                            fontSize: 16,
                            borderBottomLeftRadius: 8,
                            borderBottomRightRadius: 8,
                            textAlign: "left",
                            letterSpacing: 0.5,
                          }}
                        >
                          {item.ProductName}
                        </div>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          ))
        )}
      </Card>
      {/* Add the modal at the end of your return: */}
      <Modal
        open={showProductModal}
        onCancel={() => {
          setShowProductModal(false);
          setSelectedProductId(null);
        }}
        footer={null}
        width="90%"
        style={{ maxWidth: 900 }}
        destroyOnClose
        styles={{ padding: 0 }}
      >
        {showProductModal && selectedProductId && (
          <ProductDetail
            refreshCartCount={refreshCartCount}
            productIdQuery={selectedProductId}
            isModal
            setDocumentTitle={() => {}}
          />
        )}
      </Modal>
    </div>
  );
};

export default TrendingItemsList;
